import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useEffectivePermissions } from '../../hooks/useEffectivePermissions';
import { resolveGroups, DASHBOARD, slug, activeHref, hrefsForItem } from '../../lib/nav';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { cn } from '../../lib/utils';

/**
 * Mobile bottom navigation. Mirrors the same groups → items → tabs, in the same
 * order, as the sidebar. Tapping a group opens a bottom sheet with that group's
 * screens. 44px tap targets throughout.
 */
export function MobileBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = useEffectivePermissions();
  const [openGroup, setOpenGroup] = useState(null);

  const groups = resolveGroups({ role: user?.role, perms });
  const current = groups.find((g) => g.id === openGroup) || null;
  // Single owner href (longest match) so a prefix route never lights up two groups.
  const ownerHref = activeHref(groups, location.pathname);

  const isActive = (href) => href === ownerHref;
  const groupActive = (g) => g.items.some((it) => hrefsForItem(it).includes(ownerHref));

  const go = (href) => { setOpenGroup(null); navigate(href); };

  return (
    <>
      <nav
        className="theme-pin-light fixed bottom-0 inset-x-0 z-30 bg-slate-900 text-slate-100 border-t border-slate-800"
        data-testid="mobile-bottom-nav"
      >
        <div className="flex items-stretch overflow-x-auto scrollbar-thin">
          <NavLink
            to="/"
            end
            className={({ isActive: a }) => cn(
              'flex flex-col items-center justify-center gap-0.5 min-w-[64px] min-h-[56px] px-2 text-[10px] font-medium shrink-0',
              a ? 'text-white' : 'text-slate-400'
            )}
            data-testid="bottomnav-dashboard"
          >
            <DASHBOARD.icon className="w-5 h-5" />
            <span className="truncate max-w-[60px]">Dashboard</span>
          </NavLink>
          {groups.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => setOpenGroup(g.id)}
              className={cn(
                'flex flex-col items-center justify-center gap-0.5 min-w-[64px] min-h-[56px] px-2 text-[10px] font-medium shrink-0 transition-colors',
                groupActive(g) ? 'text-white' : 'text-slate-400 hover:text-slate-200'
              )}
              data-testid={`bottomnav-group-${g.id}`}
              aria-label={g.label}
            >
              <g.icon className="w-5 h-5" />
              <span className="truncate max-w-[60px]">{g.label}</span>
            </button>
          ))}
        </div>
      </nav>

      <Sheet open={!!openGroup} onOpenChange={(o) => !o && setOpenGroup(null)}>
        <SheetContent side="bottom" className="max-h-[70vh] overflow-y-auto" data-testid={`bottomsheet-${openGroup || 'none'}`}>
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {current && <current.icon className="w-4 h-4 opacity-70" />}
              {current?.label}
            </SheetTitle>
          </SheetHeader>
          <div className="mt-3 space-y-1 pb-4">
            {current?.items.map((it) => (
              it.isTabbed ? (
                <div key={it.id} className="py-1">
                  <div className="flex items-center gap-2 px-2 text-xs uppercase tracking-wider font-semibold text-fg-muted">
                    <it.icon className="w-3.5 h-3.5 opacity-70" />{it.name}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {it.tabs.map((t) => (
                      <button
                        key={t.href}
                        type="button"
                        onClick={() => go(t.href)}
                        className={cn('w-full text-left pl-8 pr-3 min-h-[44px] rounded-md text-sm transition-colors',
                          isActive(t.href) ? 'bg-surface-sunken text-fg font-medium' : 'text-fg-muted hover:bg-surface-sunken')}
                        data-testid={`bottomsheet-item-${slug(it.name)}-${slug(t.name)}`}
                      >
                        {t.name}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => go(it.href)}
                  className={cn('w-full flex items-center gap-3 px-2 min-h-[44px] rounded-md text-sm transition-colors',
                    isActive(it.href) ? 'bg-surface-sunken text-fg font-medium' : 'text-fg hover:bg-surface-sunken')}
                  data-testid={`bottomsheet-item-${slug(it.name)}`}
                >
                  <it.icon className="w-4 h-4 opacity-70" />{it.name}
                </button>
              )
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

export default MobileBottomNav;
